import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import { AUTH_STORAGE_KEY } from '@/constants/storage-keys';
import type { AuthSessionPayload, AuthUser } from '@/types/auth';

export interface AuthStoreState {
  accessToken: string;
  refreshToken: string;
  user: AuthUser | null;
  roles: string[];
  permissions: string[];
}

interface AuthStoreActions {
  setSession: (payload: AuthSessionPayload) => void;
  updateTokens: (accessToken: string, refreshToken: string) => void;
  setProfile: (user: AuthUser, roles: string[], permissions: string[]) => void;
  clearSession: () => void;
}

export type AuthStore = AuthStoreState & AuthStoreActions;

/** Avoid wiping an in-flight login when persisted state still shows empty credentials. Default merge favors persisted slices and would erase fresh tokens mid-rehydration. */
function mergePersistedIntoAuthStore(persistedState: unknown, currentState: AuthStore): AuthStore {
  if (
    !persistedState ||
    typeof persistedState !== 'object' ||
    Array.isArray(persistedState)
  ) {
    return currentState;
  }
  const p = persistedState as Partial<AuthStoreState>;
  const memoryRt = typeof currentState.refreshToken === 'string' ? currentState.refreshToken : '';
  if (
    memoryRt.length > 0 &&
    typeof p.refreshToken === 'string' &&
    p.refreshToken.length === 0
  ) {
    return currentState;
  }
  return { ...currentState, ...p };
}

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      accessToken: '',
      refreshToken: '',
      user: null,
      roles: [],
      permissions: [],

      setSession: (payload) =>
        set({
          accessToken: payload.accessToken,
          refreshToken: payload.refreshToken,
          user: payload.user,
          roles: payload.roles,
          permissions: payload.permissions,
        }),

      updateTokens: (accessToken, refreshToken) =>
        set((state) => ({
          ...state,
          accessToken,
          refreshToken,
        })),

      setProfile: (user, roles, permissions) =>
        set((state) => ({
          ...state,
          user,
          roles,
          permissions,
        })),

      clearSession: () =>
        set({
          accessToken: '',
          refreshToken: '',
          user: null,
          roles: [],
          permissions: [],
        }),
    }),
    {
      name: AUTH_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        user: state.user,
        roles: state.roles,
        permissions: state.permissions,
      }),
      merge: mergePersistedIntoAuthStore,
    },
  ),
);
